import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { useAuth } from "../../contexts/AuthContext";
import useAccountMutations from "../../data/useAccountMutations";
import useAccounts from "../../data/useAccounts";
import useAccountAddresses from "../../data/useAccountAddresses";
import useAccountContacts from "../../data/useAccountContacts";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useApiHelper from "../../hooks/useApiHelper";
import apiConfig from "../../utils/apiConfig";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import {
  AccountCreateEditFormFields,
  AccountStatus,
  AccountType,
  IAccount,
} from "../../models/account/IAccount";
import { PhysicalRouting } from "../../models/Part";
import {
  AccountAddressCreateEditFormFields,
  IAccountAddress,
} from "../../models/account/IAccountAddress";
import { IContact } from "../../models/Contact";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { ContactCreateEditModal } from "../contacts/ContactCreateEditModal";
import { AccountAddressCreateEditModal } from "./AccountAddressCreateEditModal";
import { AccountAddressDraftModal } from "./AccountAddressDraftModal";
import { AccountEditDetailsSection } from "./AccountEditDetailsSection";
import { AccountEditAddressesSection } from "./AccountEditAddressesSection";
import { AccountEditFinanceSection } from "./AccountEditFinanceSection";

interface AccountCreateEditFormProps {
  account?: IAccount;
  newAccountName?: string;
  /** Start a new account with the haulier flag ticked (opened from Waste Tracking). */
  presetHaulier?: boolean;
  onSaved?: () => void;
  /** Fires with the account a create just made, before onSaved. */
  onCreated?: (account: IAccount) => void;
  onCancel?: () => void;
}

type AccountEditableFormFields = AccountCreateEditFormFields & {
  accountNumber?: string;
};

type DraftAccountAddress = AccountAddressCreateEditFormFields & {
  id: string;
};

export function AccountCreateEditForm({
  account,
  newAccountName,
  presetHaulier = false,
  onSaved,
  onCreated,
  onCancel,
}: AccountCreateEditFormProps) {
  const { register, handleSubmit, reset, setValue, watch } =
    useForm<AccountEditableFormFields>();
  const { create: createAccount, createHaulier } = useAccounts("AccountCreateEditModal");
  const { accountAddresses } = useAccountAddresses(account?.id, "AccountCreateEditModal");
  const { accountContacts } = useAccountContacts(account?.id, "AccountCreateEditModal");
  const { post, put } = useApiHelper(apiConfig("AccountCreateEditModal"));
  const { update: updateAccount, updateHaulier } = useAccountMutations(
    account?.id,
    "AccountCreateEditModal"
  );
  const { saveHandlers } = useDefaultCRUDHandlers("Account");
  const { addNotification } = useNotification();
  const [primaryContact, setPrimaryContact] = useState<IContact>();
  const [isPrimaryContactOpen, setIsPrimaryContactOpen] = useState(true);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<IContact>();
  const [isAddressesOpen, setIsAddressesOpen] = useState(true);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<IAccountAddress>();
  const [draftAddressModalOpen, setDraftAddressModalOpen] = useState(false);
  const [draftAddressToEdit, setDraftAddressToEdit] = useState<DraftAccountAddress>();
  const [draftAddresses, setDraftAddresses] = useState<DraftAccountAddress[]>([]);
  const selectedPrimaryContactId = watch("primaryContactId");
  const selectedBillingAddressId = watch("billingAddressId");
  const selectedShippingAddressId = watch("shippingAddressId");
  const isModalLayout = !onCancel;

  // Yard staff only reach this form for hauliers (from Waste Tracking). Their saves go through
  // the AllStaff haulier routes, which pin the haulier flag on; admins keep the normal routes so
  // they can still untick it.
  const { claims } = useAuth();
  const haulierMode = !claims?.isAdmin && (presetHaulier || !!account?.isHaulier);
  const accountUrl = (id: string) => (haulierMode ? `/account/haulier/${id}` : `/account/${id}`);

  const accountStatusOpts = useMemo(
    () =>
      Object.values(AccountStatus).filter(
        (ct): ct is string => typeof ct === "string"
      ),
    []
  );

  const allowedAccountTypes = [AccountType.Reuse, AccountType.Remanufacturing, AccountType.Reintegration, AccountType.Recycling];

  const accountTypeOpts = useMemo(
    () =>
      Object.entries(AccountType)
        .filter(([, value]) => typeof value === "number")
        .filter((name, value) => allowedAccountTypes.includes(value))
        .map(([name, value]) => ({
          id: value as number,
          name,
        })),
    []
  );

  const physicalRoutingOpts = useMemo(
    () =>
      Object.entries(PhysicalRouting)
        .filter(([, value]) => typeof value === "number")
        .map(([name, value]) => ({
          id: value as number,
          name,
        })),
    []
  );

  const canEditAccountType = CheckFeatureFlag("TME");

  const addressOptions = useMemo(
    () =>
      (account
        ? accountAddresses.data?.data ?? []
        : draftAddresses
      ).map((address) => ({
        id: address.id,
        name: address.name,
      })),
    [account, accountAddresses.data, draftAddresses]
  );

  const primaryContactOptions = useMemo(
    () =>
      (accountContacts.data?.data ?? []).map((contact) => ({
        id: contact.id,
        name: `${contact.fullname}${contact.email ? ` - ${contact.email}` : ""}`,
      })),
    [accountContacts.data]
  );

  const selectedBillingAddress = useMemo(
    () =>
      (account ? accountAddresses.data?.data ?? [] : draftAddresses).find(
        (address) => address.id === selectedBillingAddressId
      ),
    [account, accountAddresses.data, draftAddresses, selectedBillingAddressId]
  );

  const selectedShippingAddress = useMemo(
    () =>
      (account ? accountAddresses.data?.data ?? [] : draftAddresses).find(
        (address) => address.id === selectedShippingAddressId
      ),
    [account, accountAddresses.data, draftAddresses, selectedShippingAddressId]
  );

  const onSubmit = (data: AccountEditableFormFields) => {
    const {
      accountNumber: _accountNumber,
      regularsAccount,
      ...payload
    } = data;

    if (!canEditAccountType) {
      payload.accountType = regularsAccount
        ? AccountType.Manual
        : AccountType.Auto;
    } else {
      payload.accountType ??=
        regularsAccount ? AccountType.Manual : AccountType.Auto;
    }

    if (account) {
      (haulierMode ? updateHaulier : updateAccount).mutate(
        {
          ...account,
          ...payload,
          creditLimit:
            payload.creditLimit !== "" ? parseFloat(payload.creditLimit) : 0,
        },
        {
          ...saveHandlers,
          onSuccess: () => {
            saveHandlers.onSuccess();
            if (onSaved) {
              onSaved();
            }
          },
        }
      );
    } else {
      if (!payload.primaryContactId) {
        addNotification({
          variant: "warn",
          primaryText: "Please select a primary contact",
        });
        return;
      }

      if (payload.name === "") {
        addNotification({
          variant: "warn",
          primaryText: "Please enter an account name",
        });
        return;
      }

      const parsedCreditLimit =
        payload.creditLimit !== "" ? parseFloat(payload.creditLimit) : 0;

      const createBody = {
        ...payload,
        billingAddressId: undefined,
        shippingAddressId: undefined,
        creditLimit: parsedCreditLimit,
      };
      const createOptions = {
          ...saveHandlers,
          onSuccess: async (createdRes: any) => {
            const createdAccount = createdRes?.data;

            if (createdAccount?.id) onCreated?.(createdAccount as IAccount);

            if (!createdAccount?.id) {
              saveHandlers.onSuccess();
              onSaved?.();
              return;
            }

            if (draftAddresses.length === 0) {
              saveHandlers.onSuccess();
              onSaved?.();
              return;
            }

            try {
              const idMap: Record<string, string> = {};

              for (const draft of draftAddresses) {
                const { id: draftId, ...addressPayload } = draft;
                const createdAddressRes = await post(
                  `/account/${createdAccount.id}/address`,
                  addressPayload
                );
                const createdAddressId = (createdAddressRes as any)?.data?.id as
                  | string
                  | undefined;

                if (createdAddressId) {
                  idMap[draftId] = createdAddressId;
                }
              }

              const billingAddressId = payload.billingAddressId
                ? idMap[payload.billingAddressId]
                : undefined;
              const shippingAddressId = payload.shippingAddressId
                ? idMap[payload.shippingAddressId]
                : undefined;

              if (billingAddressId || shippingAddressId) {
                await put(accountUrl(createdAccount.id), {
                  ...createdAccount,
                  ...payload,
                  creditLimit: parsedCreditLimit,
                  billingAddressId,
                  shippingAddressId,
                });
              }

              saveHandlers.onSuccess();
              onSaved?.();
            } catch (err) {
              saveHandlers.onError();
            }
          },
      };

      if (haulierMode) createHaulier.mutate(createBody, createOptions);
      else createAccount.mutate(createBody, createOptions);
    }
  };

  useEffect(() => {
    if (account) {
      reset({
        ...account,
        accountNumber: String(account.accountNumber),
        creditLimit: String(account.creditLimit),
        regularsAccount: account.accountType === AccountType.Manual,
      });
    } else {
      reset({
        name: newAccountName,
        status: 2,
        accountType: AccountType.Auto,
        physicalRoutingDefault: PhysicalRouting.None,
        regularsAccount: false,
        isHaulier: presetHaulier,
      });
      setDraftAddresses([]);
    }
  }, [account, newAccountName, presetHaulier, reset]);

  useEffect(() => {
    if (!accountContacts.data?.data || !selectedPrimaryContactId) return;

    const selected = accountContacts.data.data.find(
      (contact) => contact.id === selectedPrimaryContactId
    );

    if (selected) {
      setPrimaryContact(selected);
    }
  }, [accountContacts.data, selectedPrimaryContactId]);

  return (
    <div className={isModalLayout ? "space-y-4" : ""}>
      <ContactCreateEditModal
        open={contactModalOpen}
        setOpen={setContactModalOpen}
        contact={contactToEdit}
        onSaved={(savedContact) => {
          if (!savedContact) return;
          setPrimaryContact(savedContact);
          setValue("primaryContactId", savedContact.id);
        }}
      />
      {account && (
        <AccountAddressCreateEditModal
          open={addressModalOpen}
          setOpen={setAddressModalOpen}
          account={account}
          address={addressToEdit}
          onSaved={(savedAddress) => {
            if (!savedAddress) return;

            const savedAddressId = (savedAddress as any)?.id as string | undefined;
            if (!savedAddressId) return;

            const hasBilling = !!watch("billingAddressId");
            if (!hasBilling) {
              setValue("billingAddressId", savedAddressId);
            }
          }}
        />
      )}
      {!account && (
        <AccountAddressDraftModal
          open={draftAddressModalOpen}
          setOpen={setDraftAddressModalOpen}
          address={draftAddressToEdit}
          onSaved={(savedAddress) => {
            if (draftAddressToEdit) {
              setDraftAddresses((prev) =>
                prev.map((a) =>
                  a.id === draftAddressToEdit.id
                    ? { ...savedAddress, id: draftAddressToEdit.id }
                    : a
                )
              );
              return;
            }

            const newId = `new-${Date.now()}`;
            setDraftAddresses((prev) => [...prev, { ...savedAddress, id: newId }]);

            if (!watch("billingAddressId")) {
              setValue("billingAddressId", newId);
            }
          }}
        />
      )}
      <h2 className="text-xl font-semibold">
        {`${account ? "Edit" : "Add"} Account`}
      </h2>
      <p className="text-sm text-gray-500 -mt-1 mb-2">
        Manage account, address defaults, and finance details in one place.
      </p>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <AccountEditDetailsSection
            account={account}
            haulierLocked={haulierMode}
            register={register}
            setValue={setValue}
            watch={watch}
            accountTypeOptions={accountTypeOpts}
            physicalRoutingOptions={physicalRoutingOpts}
            canEditAccountType={canEditAccountType}
            primaryContactOptions={primaryContactOptions}
            primaryContact={primaryContact}
            setPrimaryContact={setPrimaryContact}
            isPrimaryContactOpen={isPrimaryContactOpen}
            setIsPrimaryContactOpen={setIsPrimaryContactOpen}
            onAddContact={() => {
              setContactToEdit(undefined);
              setContactModalOpen(true);
            }}
            onEditContact={() => {
              setContactToEdit(primaryContact);
              setContactModalOpen(true);
            }}
          />

          <AccountEditAddressesSection
            register={register}
            setValue={setValue}
            watch={watch}
            addressOptions={addressOptions}
            selectedBillingAddressName={selectedBillingAddress?.name}
            selectedShippingAddressName={selectedShippingAddress?.name}
            isOpen={isAddressesOpen}
            setIsOpen={setIsAddressesOpen}
            canAddAddress={true}
            onAddAddress={() => {
              if (account) {
                setAddressToEdit(undefined);
                setAddressModalOpen(true);
              } else {
                setDraftAddressToEdit(undefined);
                setDraftAddressModalOpen(true);
              }
            }}
            onEditBillingAddress={() => {
              if (!selectedBillingAddress) return;
              if (account) {
                setAddressToEdit(selectedBillingAddress as IAccountAddress);
                setAddressModalOpen(true);
              } else {
                setDraftAddressToEdit(selectedBillingAddress as DraftAccountAddress);
                setDraftAddressModalOpen(true);
              }
            }}
            onEditShippingAddress={() => {
              if (!selectedShippingAddress) return;
              if (account) {
                setAddressToEdit(selectedShippingAddress as IAccountAddress);
                setAddressModalOpen(true);
              } else {
                setDraftAddressToEdit(selectedShippingAddress as DraftAccountAddress);
                setDraftAddressModalOpen(true);
              }
            }}
            addressesLoading={account ? accountAddresses.isLoading : false}
            addAddressHelperText={undefined}
          />

          <AccountEditFinanceSection
            register={register}
            setValue={setValue}
            watch={watch}
            accountStatusOpts={accountStatusOpts}
            currentStatus={Number(watch("status"))}
          />
        </div>

        <div
          className={
            isModalLayout
              ? "sticky bottom-0 z-10 -mx-4 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6"
              : "pt-1"
          }
        >
          <div className="flex justify-between">
            {onCancel && (
              <ButtonInput
                label="Cancel"
                isSubmit={false}
                classes="mr-4"
                onClick={onCancel}
              />
            )}
            <div className="flex-1 flex justify-end">
              <ButtonInput
                label="Save"
                isSubmit={true}
                classes=""
                onClick={undefined}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

